import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { message, Button as AntButton } from "antd";
import { PageShell, PageHeader, Panel } from "../components/Page";
import { fetchSale, fetchSalePayments, downloadInvoicePdf, sendInvoiceEmail, voidSale } from "../api/auth";

export default function SaleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: sale, error } = useQuery({
    queryKey: ["sale-detail", id],
    queryFn: () => fetchSale(id!),
    enabled: !!id,
  });

  const { data: payments = [] } = useQuery({
    queryKey: ["sale-payments", id],
    queryFn: () => fetchSalePayments(id!),
    enabled: !!id && !!sale,
  });

  if (error) {
    return (
      <PageShell>
        <PageHeader
          eyebrow="Transactions"
          title="Sale Details"
          description="Sale record not found."
          actions={<AntButton onClick={() => window.history.back()}>Back</AntButton>}
        />
        <Panel flush title="Sale Detail">
          <div className="p-4 border border-stone-200 bg-stone-50 rounded-lg">
            <div className="text-stone-700 font-medium mb-2">Sale not found</div>
            <div className="text-sm text-stone-600">
              {(error as { message?: string })?.message ?? "Unknown error"}
            </div>
          </div>
        </Panel>
      </PageShell>
    );
  }

  if (!sale) {
    return (
      <PageShell>
        <PageHeader
          eyebrow="Transactions"
          title="Sale Details"
          description="Loading sale record…"
        />
        <Panel flush title="Sale Detail">
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-petrol"></div>
          </div>
        </Panel>
      </PageShell>
    );
  }

  const dueAmount = sale.dueAmount;

  return (
    <PageShell>
      <PageHeader
        eyebrow="Transactions"
        title="Sale Details"
        description={`Sale #${sale.invoiceNumber} — ${sale.listing?.title ?? "Vehicle"}`}
        actions={
          <AntButton onClick={() => window.history.back()}>Back to Sales</AntButton>
        }
      />

      <Panel flush title="Sale Detail">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <div className="text-sm text-ink-muted">Invoice</div>
            <div className="font-medium text-ink">{sale.invoiceNumber}</div>
            <div className="text-xs text-ink-muted">{sale.saleDate}</div>
          </div>
          <div>
            <div className="text-sm text-ink-muted">Buyer</div>
            <div className="font-medium text-ink">{sale.customer?.name ?? "—"}</div>
            <div className="text-xs text-ink-muted">{sale.customer?.phone ?? "—"}</div>
          </div>
          <div>
            <div className="text-sm text-ink-muted">Bike</div>
            <div className="font-medium text-ink">{sale.listing?.title ?? "Vehicle"}</div>
            <div className="text-xs text-ink-muted">{sale.listing?.publicId ?? "—"}</div>
          </div>
          <div>
            <div className="text-sm text-ink-muted">Status</div>
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11.5px] font-medium ${dueAmount > 0 ? "text-amber-700" : ""}`}>
              {sale.status}
            </span>
          </div>
        </div>

        <Panel title="Pricing Breakdown">
          <div className="space-y-4">
            <div>
              <div className="text-sm text-ink-muted">Bike price</div>
              <div className="font-medium text-2xl text-ink">৳{Number(sale.bikePrice).toLocaleString()}</div>
            </div>
            {sale.discountPercent > 0 && (
              <div>
                <div className="text-sm text-ink-muted">Discount</div>
                <div className="font-medium text-amber-600">-{sale.discountPercent}%</div>
              </div>
            )}
            <div>
              <div className="text-sm text-ink-muted">Charges total</div>
              <div className="font-medium text-2xl text-ink">৳{Number(sale.chargesTotal).toLocaleString()}</div>
            </div>
            <div className="border-t border-line pt-4">
              <div className="text-sm text-ink-muted">Total</div>
              <div className="font-medium text-2xl text-primary">৳{Number(sale.totalAmount).toLocaleString()}</div>
            </div>
          </div>
        </Panel>

        {sale.pricingBreakdown && sale.pricingBreakdown.length > 0 && (
          <Panel title="Charge Breakdown">
            <div className="overflow-x-auto">
              <table className="w-full rounded-md">
                <thead>
                  <tr className="border-b border-line">
                    <th className="text-left text-xs font-medium text-ink-muted px-6 py-3">Charge</th>
                    <th className="text-left text-xs font-medium text-ink-muted px-6 py-3">Amount</th>
                    <th className="text-left text-xs font-medium text-ink-muted px-6 py-3">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {sale.pricingBreakdown.map((c, i) => (
                    <tr key={i} className="border-b border-line">
                      <td className="text-xs font-medium text-ink px-6 py-3">{c.label}</td>
                      <td className="text-xs font-medium text-ink px-6 py-3">৳{Number(c.amount).toLocaleString()}</td>
                      <td className="text-xs font-medium text-ink-muted px-6 py-3">
                        {c.type === "NEGATIVE" ? "Deduction" : "Add-on"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        {payments.length > 0 && (
          <Panel title="Payments">
            <div className="overflow-x-auto">
              <table className="w-full rounded-md">
                <thead>
                  <tr className="border-b border-line">
                    <th className="text-left text-xs font-medium text-ink-muted px-6 py-3">Amount</th>
                    <th className="text-left text-xs font-medium text-ink-muted px-6 py-3">Method</th>
                    <th className="text-left text-xs font-medium text-ink-muted px-6 py-3">Date</th>
                    <th className="text-left text-xs font-medium text-ink-muted px-6 py-3">Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, i) => (
                    <tr key={i} className="border-b border-line">
                      <td className="text-xs font-medium text-ink px-6 py-3">৳{Number(p.amount).toLocaleString()}</td>
                      <td className="text-xs font-medium text-ink px-6 py-3">{p.method || "—"}</td>
                      <td className="text-xs font-medium text-ink-muted px-6 py-3">{p.paidAt ? new Date(p.paidAt).toLocaleDateString() : "—"}</td>
                      <td className="text-xs font-medium text-ink-muted px-6 py-3">{p.reference || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        <div className="mt-6 pt-6 border-t border-line">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-ink-muted">Total Paid</div>
              <div className="font-medium text-2xl text-primary">৳{Number(sale.paidAmount).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-ink-muted">Due</div>
              <div className={`font-medium text-2xl ${dueAmount > 0 ? "text-amber-600" : ""}`}>৳{Number(dueAmount).toLocaleString()}</div>
            </div>
          </div>
        </div>

        <Panel title="Actions">
          <div className="flex gap-3">
            <AntButton
              type="text"
              size="small"
              onClick={() => downloadInvoicePdf(sale.id).then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${sale.invoiceNo}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }).catch((err) => {
                message.error((err as { message?: string })?.message ?? "Failed to download invoice");
              })}
              title="Download invoice PDF"
            >
              Download invoice PDF
            </AntButton>
            <AntButton
              type="text"
              size="small"
              onClick={() => sendInvoiceEmail(sale.id).then(() => message.success(`Invoice emailed to ${sale.customer.email ?? "customer"}`)).catch((err) => {
                message.error((err as { message?: string })?.message ?? "Failed to send invoice email");
              })}
              title="Email invoice"
            >
              Email invoice
            </AntButton>
            <AntButton
              size="small"
              danger
              onClick={() => voidSale(sale.id, "Voided from the sale detail view").then(() => message.success("Sale voided.")).catch((err) => {
                message.error((err as { message?: string })?.message ?? "Failed to void sale.");
              })}
              title="Void sale"
            >
              Void sale
            </AntButton>
          </div>
        </Panel>
      </Panel>
    </PageShell>
  );
}